/**
 * payment-return.ts — handles the customer's return from a Stripe REDIRECT payment
 * (Revolut Pay / Klarna / redirecting 3DS). Inline (non-redirect) cards never reach here.
 *
 * The SPA bootstrap (main.ts) detects the `/booking-return` path + Stripe's appended query
 * params, stashes them (reload-surviving: history.state + sessionStorage), strips the query
 * from the URL (so the client_secret never persists in history/logs), and routes here
 * (`#/payment-return?pi=<PaymentIntent id>`).
 *
 * Flow: retrievePaymentIntent (immediate status) → for succeeded/processing, poll the public
 * payment-confirmation endpoint until the booking is `confirmed` (driven by the webhook) →
 * renderConfirmation. On a processing timeout we show "payment received, emailed shortly" —
 * never a false failure. Failed/cancelled → retry.
 *
 * The server poll only needs the `pi` (in the hash), so a refresh after the query is stripped
 * still works even without the stashed client_secret.
 */
import { state, navigate } from '../router.js';
import { renderConfirmation } from './confirmation.js';
import { getApiBase } from '../api.js';

interface StripeRetrieveResult {
  readonly paymentIntent?: { status: string };
  readonly error?: { message?: string };
}
interface StripeInstance {
  retrievePaymentIntent: (clientSecret: string) => Promise<StripeRetrieveResult>;
}

interface ConfirmationBooking {
  booking_id: string;
  status: string;
  service_name: string;
  start_time: string;
  end_time: string;
  customer_name: string;
  customer_email: string;
}
interface ConfirmationData {
  payment_status: string;
  booking: ConfirmationBooking;
}
interface ReturnStash {
  payment_intent: string;
  client_secret: string;
  redirect_status: string;
  created_at: number;
}

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 10;
const STASH_TTL_MS = 60 * 60 * 1000; // 1h

function getStripeKey(): string | undefined {
  return (window as { __TIMESLOT_CONFIG__?: { stripePublishableKey?: string } }).__TIMESLOT_CONFIG__
    ?.stripePublishableKey;
}

function loadStripe(key: string): Promise<StripeInstance | null> {
  return new Promise((resolve) => {
    const w = window as unknown as { Stripe?: (k: string) => StripeInstance };
    if (w.Stripe) {
      resolve(w.Stripe(key));
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.async = true;
    s.onload = () => resolve(w.Stripe ? w.Stripe(key) : null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
}

function piFromHash(): string | null {
  const q = window.location.hash.split('?')[1] ?? '';
  const pi = new URLSearchParams(q).get('pi');
  return pi && /^pi_[A-Za-z0-9_]{8,255}$/.test(pi) ? pi : null;
}

function readStash(pi: string): ReturnStash | null {
  const fromHistory = (window.history.state as { timeslotPaymentReturn?: ReturnStash } | null)
    ?.timeslotPaymentReturn;
  if (fromHistory && fromHistory.payment_intent === pi) return fromHistory;
  try {
    const raw = window.sessionStorage.getItem(`ts_pay_return_${pi}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReturnStash;
    if (Date.now() - parsed.created_at > STASH_TTL_MS) {
      clearStash(pi);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearStash(pi: string): void {
  try {
    window.sessionStorage.removeItem(`ts_pay_return_${pi}`);
  } catch {
    /* ignore */
  }
}

function renderMessage(
  title: string,
  body: string,
  opts: { spinner?: boolean; retry?: boolean } = {},
): void {
  const appEl = document.getElementById('app');
  if (!appEl) return;
  appEl.innerHTML = `
    <div class="ts-page ts-page--confirmation">
      <div class="ts-confirmation" role="status" aria-live="polite">
        ${opts.spinner ? '<div class="ts-confirmation__icon ts-confirmation__icon--pending" aria-hidden="true"></div>' : ''}
        <h1 class="ts-confirmation__heading" tabindex="-1">${title}</h1>
        <p class="ts-confirmation__message">${body}</p>
        ${opts.retry ? '<a class="ts-btn ts-btn--primary" href="#/services">Try again</a>' : ''}
      </div>
    </div>`;
  (appEl.querySelector('.ts-confirmation__heading') as HTMLElement | null)?.focus();
}

async function fetchConfirmation(pi: string): Promise<ConfirmationData | null> {
  try {
    const res = await fetch(
      `${getApiBase()}/payment/confirmation?payment_intent=${encodeURIComponent(pi)}`,
      { headers: { Accept: 'application/json' } },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { data?: ConfirmationData };
    return json.data ?? null;
  } catch {
    return null;
  }
}

async function pollUntilConfirmed(pi: string): Promise<boolean> {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    const data = await fetchConfirmation(pi);
    if (data && data.booking.status === 'confirmed') {
      state.lastBooking = { ...data.booking };
      renderConfirmation();
      return true;
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return false;
}

export async function renderPaymentReturn(): Promise<void> {
  const pi = piFromHash();
  if (!pi) {
    navigate('/services');
    return;
  }

  renderMessage('Confirming your payment…', 'Please wait while we confirm your booking.', {
    spinner: true,
  });

  const stash = readStash(pi);
  const stripeKey = getStripeKey();

  // Immediate provider truth via Stripe.js (read-only). Optional — the server poll below
  // works from the `pi` alone, so a missing secret/key just skips this fast path.
  let piStatus: string | null = null;
  if (stash?.client_secret && stripeKey) {
    const stripe = await loadStripe(stripeKey);
    if (stripe) {
      const result = await stripe.retrievePaymentIntent(stash.client_secret);
      piStatus = result.paymentIntent?.status ?? null;
    }
  }
  if (!piStatus && stash?.redirect_status === 'failed') piStatus = 'requires_payment_method';

  if (piStatus === 'requires_payment_method' || piStatus === 'canceled') {
    clearStash(pi);
    renderMessage(
      'Payment didn’t go through',
      'Your payment was not completed. You can try booking again — you have not been charged.',
      { retry: true },
    );
    return;
  }

  // succeeded / processing / unknown → poll the server for the webhook-driven confirmation.
  const confirmed = await pollUntilConfirmed(pi);
  if (confirmed) {
    clearStash(pi); // terminal success — no client_secret left behind
    return;
  }

  // Not yet confirmed within the window — the payment is likely still processing
  // asynchronously. Never show a false failure; the webhook will confirm + email.
  renderMessage(
    'Payment received',
    'Thank you. Your payment is being confirmed and a confirmation email will be sent to you shortly. You can safely close this page.',
  );
}
